import React, { useContext, useState, useEffect, useRef } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
  IconButton,
  MenuItem,
  FormControl,
  Select,
  ListItemText,
} from '@material-ui/core';
import { MoreVert, Replay } from '@material-ui/icons';

import { useHistory } from 'react-router-dom';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import TicketOptionsMenu from '../TicketOptionsMenu';
import ButtonWithSpinner from '../ButtonWithSpinner';
import toastError from '../../errors/toastError';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/Auth/AuthContext';
import { TicketsContext } from '../../context/Tickets/TicketsContext';

// Imports dos modais
import FileLinksModal from '../FileLinksModal';
import OrderModal from '../orderModal';
import TransferTicketModalCustom from '../TransferTicketModalCustom';
import StageActivitiesModal from '../StageActivitiesModal';
import PreRequirementsModal from '../PreRequirementsModal';

// Imports dos ícones
import ScheduleIcon from '@material-ui/icons/Schedule';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import AssignmentIcon from '@material-ui/icons/Assignment';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import Badge from '@material-ui/core/Badge';
import Tooltip from '@material-ui/core/Tooltip';
import GoogleScheduleModal from '../GoogleScheduleModal';

const useStyles = makeStyles(theme => ({
  actionButtons: {
    marginRight: 6,
    flex: 'none',
    alignSelf: 'center',
    marginLeft: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
  stageTransitionButton: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(0.5),
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500,
    minWidth: '100px',
    transition: 'all 0.2s ease',
    position: 'relative',
    zIndex: 1000,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  stageTransitionMenu: {
    '& .MuiPaper-root': {
      minWidth: '200px',
      maxHeight: '300px',
      zIndex: 9999,
    },
  },
  stageMenuItem: {
    fontSize: '0.875rem',
  },
  currentStage: {
    backgroundColor: theme.palette.action.selected,
    fontWeight: 600,
  },
}));

const TicketActionButtonsCustom = ({ ticket, contact, onStageChange }) => {
  const classes = useStyles();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stageLoading, setStageLoading] = useState(false);
  const [availableStages, setAvailableStages] = useState([]);
  const [loadingAvailableStages, setLoadingAvailableStages] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [localTicket, setLocalTicket] = useState(ticket);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectKey, setSelectKey] = useState(0);
  const [stagesRefreshKey, setStagesRefreshKey] = useState(0);
  const isMountedRef = useRef(true);
  const ticketOptionsMenuOpen = Boolean(anchorEl);
  const { user } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);

  // Detectar se o usuário é admin ou super
  useEffect(() => {
    if (user && isMountedRef.current) {
      const userIsAdmin =
        user.isAdmin || user.role === 'admin' || user.role === 'super';
      setIsAdmin(userIsAdmin);
    }
  }, [user]);

  // Sincronizar estado local com a prop ticket
  useEffect(() => {
    if (ticket && isMountedRef.current) {
      setLocalTicket(ticket);
      // Sincronizar o selectedStageId com o estágio do deal mais recente
      const getCurrentDealFromTicket = ticketData => {
        if (!ticketData) return null;

        // Se o ticket estiver fechado, não retornar deal
        if (ticketData.status === 'closed') return null;

        // Se o ticket estiver open, retornar o último deal (mais recente)
        if (ticketData.status === 'open') {
          if (!ticketData.deals || ticketData.deals.length === 0) return null;

          // Retornar o deal com maior ID (mais recente)
          return ticketData.deals.reduce((latest, current) =>
            current.id > latest.id ? current : latest
          );
        }

        return null;
      };

      const currentDeal = getCurrentDealFromTicket(ticket);
      if (currentDeal?.stage?.id) {
        setSelectedStageId(currentDeal.stage.id);
      } else {
        setSelectedStageId('');
      }
    }
  }, [ticket]);

  // Cleanup para evitar memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Função para obter o deal mais recente (ID mais alto) com status open
  const getCurrentDeal = () => {
    if (!localTicket) return null;

    // Se o ticket estiver fechado, não retornar deal
    if (localTicket.status === 'closed') return null;

    // Se o ticket estiver open, retornar o último deal (mais recente)
    if (localTicket.status === 'open') {
      if (!localTicket.deals || localTicket.deals.length === 0) return null;

      // Retornar o deal com maior ID (mais recente)
      return localTicket.deals.reduce((latest, current) =>
        current.id > latest.id ? current : latest
      );
    }

    return null;
  };

  const currentDeal = getCurrentDeal();
  const dealId = currentDeal?.id;
  // GET /tickets/:id devolve o deal com `pipelineId`; GET /tickets/u/:uuid
  // — a rota que a tela de conversa usa — devolve só o objeto `pipeline`
  // aninhado. Aceita os dois formatos.
  const currentPipelineId = currentDeal?.pipelineId ?? currentDeal?.pipeline?.id;

  useEffect(() => {
    const loadAvailableStages = async () => {
      if (dealId) {
        setLoadingAvailableStages(true);
        try {
          const response = await api.get(`/deals/${dealId}/available-stages`);

          const responseData = response.data;

          // Processar estágios baseado no tipo de usuário
          let stagesToShow = [];

          if (isAdmin) {
            // Admin/Super: pode mover para qualquer estágio
            stagesToShow = responseData.allStages || [];
          } else {
            // Usuário comum: só pode mover para next ou lost baseado em availableStages
            const availableStages = responseData.availableStages || {};
            stagesToShow = [];

            // Adicionar próximo estágio se disponível
            if (availableStages.next) {
              stagesToShow.push({
                ...availableStages.next,
                action: 'Avançar para',
                color: '#4caf50',
                canMove: availableStages.next.canMove,
              });
            }

            // Adicionar estágio perdido se disponível
            if (availableStages.lost) {
              stagesToShow.push({
                ...availableStages.lost,
                action: 'Marcar como perdido',
                color: '#f44336',
                canMove: availableStages.lost.canMove,
              });
            }
          }
          setAvailableStages(stagesToShow);
        } catch (error) {
          console.error('Erro ao buscar estágios disponíveis:', error);
          setAvailableStages([]);
          toastError(error);
        } finally {
          setLoadingAvailableStages(false);
        }
      }
    };

    loadAvailableStages();
  }, [dealId, isAdmin, stagesRefreshKey]);

  // Estados para os modais
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleQueue, setScheduleQueue] = useState([]); // Fila de produtos (serviços) para agendar após venda
  const [fileLinksModalOpen, setFileLinksModalOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [stageActivitiesModalOpen, setStageActivitiesModalOpen] =
    useState(false);
  const [contactId, setContactId] = useState(null);
  // Quantidade de atividades do estágio ainda não realizadas (badge do ícone)
  const [pendingActivitiesCount, setPendingActivitiesCount] = useState(0);

  // Estados para modal de pré-requisitos pendentes
  const [preRequirementsModalOpen, setPreRequirementsModalOpen] =
    useState(false);
  const [dealIdForPreRequirements, setDealIdForPreRequirements] =
    useState(null);
  const [targetStageIdForPreRequirements, setTargetStageIdForPreRequirements] =
    useState(null);

  const handleOpenTicketOptionsMenu = e => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseTicketOptionsMenu = e => {
    setAnchorEl(null);
  };

  // Funções para abrir modais
  const handleOpenScheduleModal = () => {
    setContactId(localTicket.contact.id);
    setScheduleQueue([]); // Abrindo manualmente: não usar fila de venda
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = result => {
    setScheduleModalOpen(false);
    setScheduleQueue(prev => {
      if (!result?.saved) {
        setContactId(null);
        setStagesRefreshKey(k => k + 1);
        return [];
      }
      const next = prev.slice(1);
      if (next.length > 0) {
        setTimeout(() => setScheduleModalOpen(true), 150);
      } else {
        setContactId(null);
        setStagesRefreshKey(k => k + 1);
      }
      return next;
    });
  };

  const handleSaleSuccessWithServices = soldServiceProducts => {
    if (!soldServiceProducts?.length) return;
    setContactId(localTicket.contact?.id || localTicket.contactId);
    const queue = soldServiceProducts.flatMap(s =>
      Array(s.quantity || 1)
        .fill()
        .map(() => ({ ...s, quantity: 1 }))
    );
    setScheduleQueue(queue);
    setTimeout(() => setScheduleModalOpen(true), 0);
  };

  const handleOpenFileLinksModal = () => {
    setContactId(localTicket.contact.id);
    setFileLinksModalOpen(true);
  };

  const handleCloseFileLinksModal = () => {
    setFileLinksModalOpen(false);
    setContactId(null);
  };

  const handleOpenOrderModal = () => {
    setContactId(localTicket.contactId);
    setOrderModalOpen(true);
  };

  const handleCloseOrderModal = () => {
    setOrderModalOpen(false);
    setContactId(null);
  };

  const handleOpenTransferModal = () => {
    setTransferModalOpen(true);
  };

  const handleCloseTransferModal = () => {
    setTransferModalOpen(false);
  };

  const handleTransferSuccess = () => {
    // Atualizar o ticket local após transferência bem-sucedida
    setLocalTicket(prev => ({
      ...prev,
      status: 'open',
      queueId: prev.queueId, // Será atualizado pelo socket
    }));
  };

  // Verificar se o ticket pertence à fila "Sem Fila"
  const isWaitingQueue = () => {
    return localTicket.queue?.name?.toLowerCase() === 'sem fila';
  };

  // Conta as atividades do estágio ainda não realizadas.
  // Usa /deals/:id/requirements — mesma fonte da aba de pendentes do
  // StageActivitiesModal. (requirements-with-filling é a aba de REALIZADAS:
  // só devolve o que já tem preenchimento, então nunca listaria pendências.)
  useEffect(() => {
    let active = true;

    const loadPendingActivities = async () => {
      if (!dealId) {
        setPendingActivitiesCount(0);
        return;
      }
      try {
        const { data } = await api.get(`/deals/${dealId}/requirements`);
        if (!active) return;

        const requirements = data.requirements || [];
        const pending = requirements.filter(
          req =>
            !req.filled &&
            (req.value === undefined ||
              req.value === null ||
              req.value === '' ||
              req.value === false)
        );
        setPendingActivitiesCount(pending.length);
      } catch (err) {
        console.warn('Erro ao contar atividades pendentes:', err);
        if (active) setPendingActivitiesCount(0);
      }
    };

    loadPendingActivities();
    return () => {
      active = false;
    };
    // Recarrega ao trocar de deal/estágio e ao fechar o modal de atividades
  }, [dealId, currentDeal?.stage?.id, stageActivitiesModalOpen]);

  const handleOpenStageActivitiesModal = () => {
    setStageActivitiesModalOpen(true);
  };

  const handleCloseStageActivitiesModal = () => {
    setStageActivitiesModalOpen(false);
    // Forçar remontagem do select de transição de estágio
    setSelectKey(prev => prev + 1);
    if (onStageChange) {
      onStageChange(localTicket, () => {
        // Callback para forçar refresh do componente
        console.log('Modal de atividades fechado - forçando refresh do select');
      });
    }
  };

  const handleStageSelectChange = event => {
    const newStageId = event.target.value;
    setSelectedStageId(newStageId);

    // Não permitir transição para o estágio atual
    if (newStageId && String(newStageId) !== String(currentDeal?.stage?.id)) {
      // Verificar se o estágio selecionado permite transição
      const selectedStage = getAvailableStages().find(
        stage => String(stage.id) === String(newStageId)
      );
      if (selectedStage && selectedStage.canTransition) {
        handleStageTransition(newStageId);
      }
    }
  };

  // Função para obter estágios disponíveis
  const getAvailableStages = () => {
    if (!currentDeal?.pipeline) return [];

    const currentStageId = currentDeal?.stage?.id;

    // Retornar apenas os estágios disponíveis para transição
    if (availableStages.length > 0) {
      const stages = availableStages.map(stage => ({
        ...stage,
        isCurrent: stage.id === currentStageId,
        canTransition: stage.canMove !== false,
      }));
      return stages;
    }

    return [];
  };
  // Função para verificar pré-requisitos antes de mover para "Perdidos"
  const checkPreRequirementsBeforeMove = async (dealId, pipelineId) => {
    try {
      // 1. Buscar pré-requisitos configurados para "Perdidos" na pipeline
      const { data: preReqData } = await api.get(
        `/pipelines/${pipelineId}/lost-stage-pre-requirements`
      );
      const configuredPreRequirements = preReqData.preRequirements || [];

      if (configuredPreRequirements.length === 0) {
        // Não há pré-requisitos configurados, pode mover
        return { canMove: true, unmetRequirements: [] };
      }

      // 2. Buscar requirements do deal
      const { data: requirementsData } = await api.get(
        `/deals/${dealId}/requirements`
      );
      const dealRequirements = requirementsData.requirements || [];

      // 3. Criar um mapa dos requirements do deal por ID
      const dealRequirementsMap = new Map(
        dealRequirements.map(req => [req.id, req])
      );

      // 4. Filtrar apenas os pré-requisitos configurados que são obrigatórios e não estão preenchidos
      const unmetRequirements = configuredPreRequirements
        .map(preReq => {
          const requirement =
            preReq.requirement || dealRequirementsMap.get(preReq.requirementId);
          if (!requirement) return null;

          // Verificar se está preenchido
          const isFilled =
            requirement.filled ||
            (requirement.value !== null &&
              requirement.value !== undefined &&
              requirement.value !== '' &&
              requirement.value !== false);

          // Retornar apenas se for obrigatório e não estiver preenchido
          if (requirement.required && !isFilled) {
            return requirement;
          }
          return null;
        })
        .filter(req => req !== null);

      return {
        canMove: unmetRequirements.length === 0,
        unmetRequirements,
      };
    } catch (err) {
      console.error('Erro ao verificar pré-requisitos:', err);
      // Em caso de erro, permitir movimento (não bloquear)
      return { canMove: true, unmetRequirements: [] };
    }
  };

  // Função para atualizar estágio
  const updateDealStage = async (dealId, newStageId) => {
    try {
      await api.patch(`/deals/${dealId}/stages/${newStageId}`);
      return true;
    } catch (err) {
      // Log para debug
      console.log('Erro ao mover deal:', err.response?.data);

      // Verificar se é erro de pré-requisitos - verificar múltiplas possibilidades
      const errorCode =
        err.response?.data?.code ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        '';
      const isPreRequirementError =
        errorCode === 'ERR_CANNOT_MOVE_TO_PERDIDOS_WITHOUT_PRE_REQUIREMENTS' ||
        errorCode.includes('ERR_CANNOT_MOVE_TO_PERDIDOS') ||
        errorCode.includes('PRE_REQUIREMENTS') ||
        err.response?.data?.message?.includes('pré-requirement') ||
        err.response?.data?.message?.includes('pre-requirement') ||
        err.response?.data?.message?.includes('pre requirement');

      if (isPreRequirementError) {
        // Abrir modal de pré-requisitos
        console.log('Abrindo modal de pré-requisitos para deal:', dealId);
        setDealIdForPreRequirements(dealId);
        setTargetStageIdForPreRequirements(newStageId);
        setPreRequirementsModalOpen(true);
        return false;
      }

      toast.error('Erro ao mover card. Tente novamente.');
      return false;
    }
  };

  // Função para executar transição de estágio
  const handleStageTransition = async targetStageId => {
    if (!currentDeal?.id) {
      toast.error('Deal não encontrado');
      return;
    }

    // 1. Verificar se o estágio destino é "Perdidos"
    const targetStage = currentDeal?.pipeline?.stages?.find(
      s => s.id === targetStageId
    );
    const isPerdidosStage =
      targetStage &&
      (targetStage.name.toLowerCase().includes('perdido') ||
        targetStage.name.toLowerCase().includes('lost'));

    // 2. Se for estágio "Perdidos", verificar pré-requisitos ANTES de tentar mover
    if (isPerdidosStage && currentDeal?.pipelineId) {
      const preReqCheck = await checkPreRequirementsBeforeMove(
        currentDeal.id,
        currentDeal.pipelineId
      );

      if (!preReqCheck.canMove) {
        // Há pré-requisitos obrigatórios não preenchidos
        // Abrir modal e NÃO fazer a requisição de movimento
        console.log('Pré-requisitos não cumpridos, abrindo modal sem mover');
        setDealIdForPreRequirements(currentDeal.id);
        setTargetStageIdForPreRequirements(targetStageId);
        setPreRequirementsModalOpen(true);
        // Resetar o select para o estágio atual
        setSelectedStageId(currentDeal?.stage?.id || '');
        return;
      }
      // Se todos os pré-requisitos estão preenchidos, continuar com o movimento
    }

    // 3. Atualizar no backend
    setStageLoading(true);
    try {
      const success = await updateDealStage(currentDeal.id, targetStageId);

      if (!success) {
        // Se falhar ao atualizar, o modal de pré-requisitos já foi mostrado se necessário
        // Resetar o select para o estágio atual
        setSelectedStageId(currentDeal?.stage?.id || '');
        setStageLoading(false);
        return;
      }

      const response = await api.get(`/deals/${currentDeal.id}`);
      const dealData = response.data;

      if (isMountedRef.current) {
        // Obter o estágio de destino
        const updatedTargetStage =
          dealData?.stage ||
          currentDeal?.pipeline?.stages?.find(s => s.id === targetStageId);

        // Verificar se o estágio é "concluído" baseado no nome
        const isCompletedStage =
          updatedTargetStage?.name?.toLowerCase().includes('concluído') ||
          updatedTargetStage?.name?.toLowerCase().includes('concluido') ||
          updatedTargetStage?.name?.toLowerCase().includes('finalizado') ||
          updatedTargetStage?.name?.toLowerCase().includes('resolvido');

        // Se for estágio concluído, fechar o ticket
        if (isCompletedStage) {
          try {
            await api.put(`/tickets/${localTicket.id}`, {
              status: 'closed',
              userId: user?.id,
              useIntegration: false,
              promptId: null,
              integrationId: null,
            });
          } catch (ticketError) {
            console.error(
              '❌ [Stage Transition] Erro ao fechar ticket:',
              ticketError
            );
            // Continuar mesmo se houver erro ao fechar o ticket
          }
        }

        // Atualizar o ticket local com os dados da resposta
        const updatedTicket = {
          ...localTicket,
          status: isCompletedStage ? 'closed' : localTicket.status,
          deals: localTicket.deals?.map(deal =>
            deal.id === currentDeal.id
              ? {
                  ...dealData,
                  stageId: targetStageId,
                  stage: updatedTargetStage,
                }
              : deal
          ) || [
            { ...dealData, stageId: targetStageId, stage: updatedTargetStage },
          ],
        };

        // Atualizar o estado local
        setLocalTicket(updatedTicket);

        // Atualizar o contexto global
        setCurrentTicket(updatedTicket);

        // Atualizar selectedStageId para o novo estágio
        setSelectedStageId(targetStageId);

        // Notificar sobre a mudança de estágio
        if (onStageChange) {
          onStageChange(updatedTicket, () => {
            // Callback para forçar refresh do componente de conversas
            // Similar ao fluxo de transferência de leads
          });
        }

        // Mostrar toast de sucesso
        const message = isCompletedStage
          ? 'Deal concluído e ticket fechado'
          : 'Movido para o próximo estágio';
        toast.success(message);
      }
    } catch (error) {
      console.error('❌ [Stage Transition] Erro ao mover deal:', error);
      if (isMountedRef.current) {
        toastError(error);
        // Resetar o select para o estágio atual em caso de erro
        setSelectedStageId(currentDeal?.stage?.id || '');
      }
    } finally {
      if (isMountedRef.current) {
        setStageLoading(false);
      }
    }
  };

  return (
    <div className={classes.actionButtons}>
      {localTicket.status === 'closed' && (
        <ButtonWithSpinner
          loading={loading}
          startIcon={<Replay />}
          size='small'
          onClick={e => {
            setLoading(true);
            try {
              api
                .put(`/tickets/${localTicket.id}`, {
                  status: 'open',
                  userId: user?.id,
                  useIntegration: localTicket.useIntegration,
                  promptId: localTicket.promptId,
                  integrationId: localTicket.integrationId,
                })
                .then(() => {
                  setLoading(false);
                  setCurrentTicket({ ...ticket, code: '#open' });
                })
                .catch(err => {
                  setLoading(false);
                  toastError(err);
                });
            } catch (err) {
              setLoading(false);
              toastError(err);
            }
          }}
        >
          {i18n.t('messagesList.header.buttons.reopen')}
        </ButtonWithSpinner>
      )}

      {localTicket.status === 'open' && (
        <>
          {/* Select de Transição de Estágio - usar deal ativo */}
          {currentDeal?.pipeline && (
            <div
              style={{ width: 160, marginTop: -4, marginRight: 8 }}
              key={`select-${selectKey}`}
            >
              <FormControl fullWidth margin='dense'>
                <Select
                  displayEmpty
                  variant='outlined'
                  value={String(selectedStageId) || ''}
                  onChange={handleStageSelectChange}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                  }}
                  renderValue={() => currentDeal?.stage?.name || 'Estágio'}
                  disabled={stageLoading || loadingAvailableStages}
                  style={{ fontSize: '0.875rem' }}
                >
                  {getAvailableStages().length > 0 &&
                    getAvailableStages().map(stage => (
                      <MenuItem
                        dense
                        key={stage.id}
                        value={String(stage.id)}
                        disabled={!stage.canTransition}
                        style={{
                          fontSize: '0.875rem',
                          color: stage.color || '#666',
                          opacity: !stage.canTransition ? 0.6 : 1,
                          transition: 'background-color 0.2s ease',
                        }}
                        onMouseEnter={e => {
                          if (stage.canTransition) {
                            // Cor mais visível para o light mode
                            e.target.style.backgroundColor =
                              'rgba(0, 0, 0, 0.15)';
                          }
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        {stage.name}
                      </MenuItem>
                    ))}
                  {getAvailableStages().length === 0 &&
                    !loadingAvailableStages && (
                      <MenuItem disabled>
                        <ListItemText primary='Nenhum estágio disponível' />
                      </MenuItem>
                    )}
                  {loadingAvailableStages && (
                    <MenuItem disabled>
                      <ListItemText primary='Carregando estágios...' />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </div>
          )}
          {/* Ícones dos modais - usar deal ativo (ticket pode ter vários deals, apenas um ativo) */}
          {/* Botão de Agendamento - apenas quando há requirement de schedule */}
          {currentDeal?.stage?.requirements?.some(
            req => req.type === 'schedule'
          ) && (
            <Tooltip title={i18n.t('ticketOptionsMenu.schedule')}>
              <IconButton onClick={handleOpenScheduleModal} color='primary'>
                <ScheduleIcon />
              </IconButton>
            </Tooltip>
          )}

          {currentDeal?.stage?.requirements?.some(
            req => req.type === 'attach'
          ) && (
            <Tooltip title={i18n.t('ticketOptionsMenu.attachment')}>
              <IconButton onClick={handleOpenFileLinksModal} color='primary'>
                <AttachFileIcon />
              </IconButton>
            </Tooltip>
          )}

          {currentDeal?.stage?.requirements?.some(
            req => req.type === 'sale'
          ) && (
            <Tooltip title={i18n.t('ticketOptionsMenu.cart')}>
              <IconButton onClick={handleOpenOrderModal} color='primary'>
                <ShoppingCartIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip
            title={
              pendingActivitiesCount > 0
                ? `${i18n.t('ticketOptionsMenu.stageActivities')} — ${
                    pendingActivitiesCount
                  } pendente${pendingActivitiesCount > 1 ? 's' : ''}`
                : i18n.t('ticketOptionsMenu.stageActivities')
            }
          >
            <IconButton
              onClick={handleOpenStageActivitiesModal}
              color='primary'
            >
              <Badge
                badgeContent={pendingActivitiesCount}
                color='error'
                overlap='rectangular'
              >
                <AssignmentIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {currentPipelineId && (
            <Tooltip title='Abrir lead na pipeline'>
              <IconButton
                onClick={() =>
                  history.push(
                    `/crm/${currentPipelineId}?dealId=${currentDeal.id}`
                  )
                }
                color='primary'
              >
                <AccountTreeIcon />
              </IconButton>
            </Tooltip>
          )}

          <IconButton onClick={handleOpenTicketOptionsMenu}>
            <MoreVert />
          </IconButton>
          <TicketOptionsMenu
            ticket={ticket}
            anchorEl={anchorEl}
            menuOpen={ticketOptionsMenuOpen}
            handleClose={handleCloseTicketOptionsMenu}
          />
        </>
      )}
      {localTicket.status === 'pending' && (
        <ButtonWithSpinner
          loading={loading}
          size='small'
          variant='contained'
          color='primary'
          onClick={e => {
            // Permitir aceitar diretamente, mesmo se for da fila Sem Fila
            setLoading(true);
            try {
              api
                .put(`/tickets/${localTicket.id}`, {
                  status: 'open',
                  userId: user?.id,
                  useIntegration: localTicket.useIntegration,
                  promptId: localTicket.promptId,
                  integrationId: localTicket.integrationId,
                })
                .then(() => {
                  setLoading(false);
                  setCurrentTicket({ ...ticket, code: '#open' });
                })
                .catch(err => {
                  setLoading(false);
                  toastError(err);
                });
            } catch (err) {
              setLoading(false);
              toastError(err);
            }
          }}
        >
          {i18n.t('messagesList.header.buttons.accept')}
        </ButtonWithSpinner>
      )}
      {/* Modais */}
      <GoogleScheduleModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        aria-labelledby='form-dialog-title'
        contactId={contactId}
        ticket={ticket}
        dealId={currentDeal?.id || ticket?.dealId || ticket?.deal?.id}
        contact={contact || localTicket?.contact}
        initialProductToSchedule={scheduleQueue[0] || undefined}
      />
      <FileLinksModal
        onClose={handleCloseFileLinksModal}
        open={fileLinksModalOpen}
        ticket={ticket}
      />
      <OrderModal
        open={orderModalOpen}
        onClose={handleCloseOrderModal}
        contactId={contactId}
        ticketId={localTicket?.id}
        dealId={currentDeal?.id}
        onSaleSuccess={handleSaleSuccessWithServices}
      />
      <StageActivitiesModal
        open={stageActivitiesModalOpen}
        onClose={handleCloseStageActivitiesModal}
        ticket={ticket}
      />

      {/* Modal de Transferência para tickets da fila Sem Fila */}
      <TransferTicketModalCustom
        modalOpen={transferModalOpen}
        onClose={handleCloseTransferModal}
        ticketid={localTicket.id}
        onTransferSuccess={handleTransferSuccess}
      />

      {/* Modal de Pré-requisitos Pendentes */}
      <PreRequirementsModal
        open={preRequirementsModalOpen}
        onClose={() => {
          setPreRequirementsModalOpen(false);
          setDealIdForPreRequirements(null);
          setTargetStageIdForPreRequirements(null);
        }}
        dealId={dealIdForPreRequirements}
        targetStageId={targetStageIdForPreRequirements}
        onRequirementsFilled={async (filledDealId, filledStageId) => {
          // Fazer a requisição de movimento APENAS após fechar o modal e preencher os pré-requisitos
          try {
            console.log(
              'Movendo deal após preencher pré-requisitos:',
              filledDealId,
              filledStageId
            );
            const success = await updateDealStage(filledDealId, filledStageId);
            if (success) {
              // Recarregar o deal após mover
              const response = await api.get(`/deals/${filledDealId}`);
              const dealData = response.data;

              if (isMountedRef.current) {
                const updatedTicket = {
                  ...localTicket,
                  deals: localTicket.deals?.map(deal =>
                    deal.id === filledDealId ? dealData : deal
                  ) || [dealData],
                };

                setLocalTicket(updatedTicket);
                setCurrentTicket(updatedTicket);

                // Atualizar selectedStageId para o novo estágio
                setSelectedStageId(filledStageId);

                // Forçar remontagem do select
                setSelectKey(prev => prev + 1);

                if (onStageChange) {
                  onStageChange(updatedTicket, () => {});
                }

                toast.success('Deal movido para "Perdidos" com sucesso!');
              }
            } else {
              toast.error(
                'Erro ao mover deal. Verifique se todos os pré-requisitos foram preenchidos.'
              );
            }
          } catch (error) {
            console.error(
              'Erro ao mover deal após preencher pré-requisitos:',
              error
            );
            toast.error('Erro ao mover deal. Tente novamente.');
          }
        }}
      />
    </div>
  );
};

export default TicketActionButtonsCustom;
